import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, UserPlus, Clock, Check, Users, MessageCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DiscoverPage({ user }) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('suggested');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const [suggestRes, allRes] = await Promise.all([
        axios.get(`${API}/friends/suggestions?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/users/all`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setSuggestions(suggestRes.data);
      setAllUsers(allRes.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const sendRequest = async (userId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/friends/request/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s =>
        s.id === userId ? { ...s, friendship_status: 'requested' } : s
      ));
      toast.success('Devotee request sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
  };

  const cancelRequest = async (userId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/friends/cancel/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(prev => prev.map(s =>
        s.id === userId ? { ...s, friendship_status: 'none' } : s
      ));
      toast.success('Request cancelled');
    } catch (err) {
      toast.error('Failed to cancel request');
    }
  };

  const handleMessage = async (userId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/conversations/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/messages/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to start conversation');
    }
  };

  const getFilteredData = useCallback(() => {
    const dataset = activeTab === 'suggested' ? suggestions : allUsers;
    let result = dataset;

    if (filter !== 'all') {
      result = result.filter(u => u.role === filter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(u =>
        u.name.toLowerCase().includes(q) ||
        (u.email && u.email.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeTab, suggestions, allUsers, filter, searchQuery]);

  const filteredData = getFilteredData();

  const getRoleBadge = (role) => {
    switch (role) {
      case 'pandit':
        return <span className="px-2 py-0.5 bg-[#FF9933]/15 text-[#FF9933] text-[11px] rounded-full font-semibold">Pandit</span>;
      case 'creator':
        return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[11px] rounded-full font-semibold">Creator</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-[#FF9933] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Finding devotees...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-2 sm:px-4 py-4 pb-24 md:pb-8" data-testid="discover-page">
      {/* Header */}
      <div className="mb-4">
        <h1
          className="text-2xl sm:text-3xl font-bold text-gray-900"
          style={{ fontFamily: 'Merriweather, serif' }}
          data-testid="discover-heading"
        >
          Discover Devotees
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Connect with {allUsers.length} members of our spiritual community
        </p>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9933]/40 border border-gray-200 focus:border-[#FF9933]"
              data-testid="discover-search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {[
              { value: 'all', label: 'All' },
              { value: 'user', label: 'Devotees' },
              { value: 'pandit', label: 'Pandits' },
              { value: 'creator', label: 'Creators' },
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  filter === f.value
                    ? 'bg-[#FF9933] text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-white rounded-xl shadow-sm border border-gray-100 p-1">
        <button
          onClick={() => setActiveTab('suggested')}
          data-testid="tab-suggested"
          className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'suggested'
              ? 'bg-[#FF9933] text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className="flex items-center justify-center gap-1.5">
            <UserPlus className="w-4 h-4" />
            People You May Know
          </span>
        </button>
        <button
          onClick={() => setActiveTab('all')}
          data-testid="tab-all-members"
          className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
            activeTab === 'all'
              ? 'bg-[#FF9933] text-white shadow-sm'
              : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className="flex items-center justify-center gap-1.5">
            <Users className="w-4 h-4" />
            All Members ({allUsers.length})
          </span>
        </button>
      </div>

      {/* Results */}
      {filteredData.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No devotees found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search or filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="discover-results">
          {filteredData.map((member) => (
            <div
              key={member.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow group"
              data-testid={`devotee-card-${member.id}`}
            >
              {/* Top: Avatar + Info */}
              <div className="flex items-start gap-3 mb-3">
                <button
                  onClick={() => navigate(`/profile/${member.id}`)}
                  className="flex-shrink-0"
                >
                  <img
                    src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=FF9933&color=fff`}
                    alt={member.name}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-[#FF9933]/30 transition-all"
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <button
                    onClick={() => navigate(`/profile/${member.id}`)}
                    className="text-left"
                  >
                    <h3 className="font-semibold text-gray-900 text-sm truncate hover:text-[#FF9933] transition-colors">
                      {member.name}
                    </h3>
                  </button>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {getRoleBadge(member.role)}
                    {member.mutual_friends_count > 0 && (
                      <span className="text-[11px] text-gray-500 flex items-center gap-0.5">
                        <Users className="w-3 h-3" />
                        {member.mutual_friends_count} mutual
                      </span>
                    )}
                  </div>
                  {member.bio && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{member.bio}</p>
                  )}
                  {member.mutual_friend_names && member.mutual_friend_names.length > 0 && (
                    <p className="text-[11px] text-gray-400 mt-1 truncate">
                      Including {member.mutual_friend_names.join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Bottom: Action Buttons */}
              {member.id !== user?.id ? (
                <div className="flex gap-2">
                  {member.friendship_status === 'none' && (
                    <button
                      onClick={() => sendRequest(member.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#FF9933] hover:bg-[#E68A00] text-white text-sm font-medium rounded-lg transition-colors"
                      data-testid={`add-friend-${member.id}`}
                    >
                      <UserPlus className="w-4 h-4" />
                      Add Devotee
                    </button>
                  )}
                  {member.friendship_status === 'requested' && (
                    <button
                      onClick={() => cancelRequest(member.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg transition-colors"
                      data-testid={`cancel-request-${member.id}`}
                    >
                      <Clock className="w-4 h-4" />
                      Requested
                    </button>
                  )}
                  {member.friendship_status === 'pending' && (
                    <button
                      onClick={() => navigate(`/profile/${member.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-50 hover:bg-green-100 text-green-700 text-sm font-medium rounded-lg transition-colors"
                    >
                      <Check className="w-4 h-4" />
                      Respond
                    </button>
                  )}
                  {(!member.friendship_status || member.friendship_status === 'friends') && activeTab === 'all' && (
                    <button
                      onClick={() => navigate(`/profile/${member.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                    >
                      View Profile
                    </button>
                  )}
                  <button
                    onClick={() => handleMessage(member.id)}
                    className="p-2 bg-gray-100 hover:bg-[#FF9933]/10 hover:text-[#FF9933] text-gray-500 rounded-lg transition-colors"
                    title="Message"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center py-1">
                  <span className="text-xs text-gray-400 bg-gray-50 px-3 py-1 rounded-full">This is you</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
